import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { environment } from '@root/src/environments/environment';
import { ensureOpenLayers } from './orchestration-openlayers.util';

declare const ol: any;

@Component({
  selector: 'app-orchestration-aoi-map',
  standalone: true,
  imports: [TranslateModule, SecondaryButton],
  templateUrl: './orchestration-aoi-map.html',
  styleUrl: './orchestration-aoi-map.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationAoiMapComponent implements AfterViewInit, OnDestroy {
  readonly wkt = input('');
  readonly wktChange = output<string>();

  private readonly host = viewChild<ElementRef<HTMLElement>>('mapHost');
  private readonly destroyRef = inject(DestroyRef);
  readonly drawing = signal(false);

  private map: any;
  private vectorSource: any;
  private drawInteraction: any;
  private modifyInteraction: any;
  private resizeObserver: ResizeObserver | null = null;
  private applyingExternal = false;

  constructor() {
    effect(() => {
      const value = this.wkt();
      if (!this.map || this.applyingExternal) {
        return;
      }
      this.renderWkt(value);
    });
  }

  ngAfterViewInit(): void {
    void ensureOpenLayers().then(() => this.initMap());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.clearInteractions();
    this.map?.setTarget(undefined);
    this.map = null;
  }

  startDraw(): void {
    if (!this.map) {
      return;
    }
    this.clearInteractions();
    this.vectorSource.clear();
    this.drawing.set(true);
    this.drawInteraction = new ol.interaction.Draw({
      source: this.vectorSource,
      type: 'Polygon',
    });
    this.drawInteraction.on('drawend', (event: { feature: unknown }) => {
      this.drawing.set(false);
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
      this.enableModify();
      this.emitFeature(event.feature);
    });
    this.map.addInteraction(this.drawInteraction);
  }

  clearPolygon(): void {
    this.clearInteractions();
    this.vectorSource?.clear();
    this.drawing.set(false);
    this.emitWkt('');
  }

  private initMap(): void {
    const target = this.host()?.nativeElement;
    if (!target || this.map || typeof ol === 'undefined') {
      return;
    }
    this.vectorSource = new ol.source.Vector();
    const vectorLayer = new ol.layer.Vector({
      source: this.vectorSource,
      style: new ol.style.Style({
        fill: new ol.style.Fill({ color: 'rgba(37, 99, 235, 0.25)' }),
        stroke: new ol.style.Stroke({ color: '#2563eb', width: 2 }),
      }),
    });
    const center = environment.map.center ?? { lat: environment.map.lat, lng: environment.map.lng };
    const controls = typeof ol.control?.defaults === 'function'
      ? ol.control.defaults({ attribution: false, rotate: false })
      : undefined;
    this.map = new ol.Map({
      target,
      layers: [
        new ol.layer.Tile({ source: new ol.source.OSM() }),
        vectorLayer,
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat([center.lng, center.lat]),
        zoom: environment.map.zoom ?? 6,
      }),
      ...(controls ? { controls } : {}),
    });
    this.renderWkt(this.wkt());
    this.resizeObserver = new ResizeObserver(() => this.map?.updateSize());
    this.resizeObserver.observe(target);
    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
    setTimeout(() => this.map?.updateSize(), 200);
  }

  private renderWkt(wkt: string): void {
    if (!this.vectorSource || typeof ol === 'undefined') {
      return;
    }
    this.clearInteractions();
    this.vectorSource.clear();
    if (!wkt.trim()) {
      return;
    }
    try {
      const feature = new ol.format.WKT().readFeature(wkt, {
        dataProjection: 'EPSG:4326',
        featureProjection: this.map.getView().getProjection(),
      });
      this.vectorSource.addFeature(feature);
      const extent = feature.getGeometry()?.getExtent();
      if (extent) {
        this.map.getView().fit(extent, { padding: [32, 32, 32, 32], maxZoom: 14, duration: 250 });
      }
      this.enableModify();
    } catch {
      this.vectorSource.clear();
    }
  }

  private enableModify(): void {
    if (!this.map || this.modifyInteraction) {
      return;
    }
    this.modifyInteraction = new ol.interaction.Modify({ source: this.vectorSource });
    this.modifyInteraction.on('modifyend', () => {
      const feature = this.vectorSource.getFeatures()[0];
      if (feature) {
        this.emitFeature(feature);
      }
    });
    this.map.addInteraction(this.modifyInteraction);
  }

  private emitFeature(feature: unknown): void {
    const wkt = new ol.format.WKT().writeFeature(feature, {
      dataProjection: 'EPSG:4326',
      featureProjection: this.map.getView().getProjection(),
      decimals: 6,
    });
    this.emitWkt(wkt);
  }

  private emitWkt(wkt: string): void {
    this.applyingExternal = true;
    this.wktChange.emit(wkt);
    queueMicrotask(() => {
      this.applyingExternal = false;
    });
  }

  private clearInteractions(): void {
    if (!this.map) {
      return;
    }
    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }
    if (this.modifyInteraction) {
      this.map.removeInteraction(this.modifyInteraction);
      this.modifyInteraction = null;
    }
  }
}
